import { useQuery } from '@tanstack/react-query';
import { enqueueSnackbar } from 'notistack';

import { NavbarFlags } from '@/types/navbarFlags';

import { AccountType } from '@/api/enums/supabase';
import { PropertyLocationModel } from '@/api/models/propertyLocation';
import { getQueryKey } from '@/api/utils/getQueryKey';
import { getResolvedEndpoint } from '@/api/utils/getResolvedEndpoint';
import { handleApiCall } from '@/api/utils/handleApiCall';
import { useAuth } from '@/context/AuthProvider';

export type ApiResponse_GetNavbarFlags_PropertyManager = NavbarFlags;

const METHOD = 'GET';
const ENDPOINT = 'property-manager/navbar-flags/:locationId';
export const QUERY_KEY = (locationId?: PropertyLocationModel['id']) =>
  getQueryKey(METHOD, ENDPOINT, { locationId });

export const useGetNavbarFlags = (locationId?: PropertyLocationModel['id'], enabled = true) => {
  const { user, session } = useAuth();

  return useQuery({
    enabled: enabled && !!session?.access_token && !!locationId,
    queryKey: QUERY_KEY(locationId),
    queryFn: async () => {
      try {
        if (user?.user_metadata.account_type !== AccountType.property_manager || !locationId) {
          return {
            showCommunicationMenuItems: true,
            showWorkflowsMenuItem: true,
            showPropertyInvoices: true,
            showMaintenanceRequestsMenuItem: true,
          } as ApiResponse_GetNavbarFlags_PropertyManager;
        }

        const data = await handleApiCall<ApiResponse_GetNavbarFlags_PropertyManager>(
          session,
          METHOD,
          getResolvedEndpoint(ENDPOINT, {
            locationId: locationId.toString(),
          })
        );

        return data;
      } catch (err) {
        enqueueSnackbar('Failed to fetch navbar flags.', { variant: 'error' });

        throw err;
      }
    },
  });
};
